/**
 * 🔴 LAKH GROUPING — ONE DEFINITION, SHARED BY THE SERVER AND EVERY FRONT END.
 *
 * ERP Phase 1a increment 1. This code did not change when it moved here; what changed is that there
 * is now exactly one copy of it.
 *
 * ## ⚠️ THERE WERE TWO, AND THE SECOND ONE REACHED A CUSTOMER
 *
 * `web/src/components/kit.tsx` carried a private copy, and its own comment said why:
 *
 *   > *"Deliberately a copy of the server's `groupDigits` rather than an import: **this is a
 *   > separate build with no shared package**, and the two are pinned together by the comment on
 *   > each."*
 *
 * 🔴 The pinning failed before it was written down. The same file records the failure: *"the same
 * balance read `134,000.00` in the browser and `1,34,000.00` on the document being sent to the
 * customer. One product, one figure, two conventions — and the wrong one was the one on screen all
 * day."* 🔬 A comment is what pinned them together, and a comment is not a mechanism.
 *
 * ## 🔧 SO THE CONDITION THAT COMMENT NAMED HAS BEEN MET
 *
 * It said *"no shared package"*. There is one now, and the copy is gone rather than re-pinned. When
 * a second jurisdiction lands, the server threads a per-company `GroupingStyle` and the front ends
 * take it as a prop — which is a change in ONE place, which is the whole point.
 */

/**
 * Digit grouping style. Bangladesh (like India) groups the last three digits and then in pairs —
 * ৳ 1,05,000.00, not ৳ 105,000.00 — which is what NBR forms and every BD accountant expect.
 * `western` is kept for non-BD locales once jurisdiction packs land.
 */
export type GroupingStyle = 'lakh' | 'western';

/**
 * Document grouping. BD is the product's first (and currently only) jurisdiction.
 *
 * A CONSTANT, not a mutable module-level variable with a setter. The setter had no caller anywhere
 * in `src/`, and wiring one per request — which is what it was there for — would have changed
 * number formatting for every other request in flight, since the value is shared by the whole
 * process. When a second jurisdiction lands, it becomes a per-company setting threaded through
 * like the base currency and the timezone, not a global somebody assigns to.
 */
const DEFAULT_GROUPING: GroupingStyle = 'lakh';

export function getDefaultGrouping(): GroupingStyle {
  return DEFAULT_GROUPING;
}

/** Group an unsigned integer string: `western` = 3,3,3…; `lakh` = last 3, then 2,2,2… */
export function groupDigits(intDigits: string, style: GroupingStyle = DEFAULT_GROUPING): string {
  if (style === 'western') return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (intDigits.length <= 3) return intDigits;
  const last3 = intDigits.slice(-3);
  const rest = intDigits.slice(0, -3);
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
}
